import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11 } from "styled-components";
import type { SanityCaseStudyCard } from "@ouster/shared";
import React from "react";
import { type Fetcher } from "swr";
import useSWRInfinite from "swr/infinite";
import { hasItems } from "~/features/app/utils";
import { CaseStudyCard } from "~/features/card/case-study-card";
import { CaseStudiesMap } from "~/features/case-study/case-studies-map";
import { buildCaseStudiesUrl } from "~/features/case-study/case-studies-url";
import { NoResults } from "~/features/filter/no-results";
import { Grid } from "~/features/ui/grid";
import { LoadMore } from "~/features/ui/load-more";
import { getThemeStyle } from "~/features/ui/get-theme-style";
import { useInfiniteScroll } from "~/features/ui/use-infinite-scroll";
import { Wrap } from "~/features/ui/wrap";
type CaseStudiesPage = {
  success?: boolean;
  data?: SanityCaseStudyCard[];
  total?: number;
};
type Props = {
  limit: number;
  total: number;
  initialCaseStudies: SanityCaseStudyCard[];
  activeIndustry?: string;
  middleBlocks?: React.ReactNode;
  onClearFilters: () => void;
};

/**
 * Design 3.0 listing geometry: gaps and outer padding live on the grid, never on
 * the cells, so a `lg:col-span-4` card is exactly one design column wide (496px
 * at the 1520px wrap) and its media fills that column. Cells stay unpainted and
 * unbordered so the route's dotted `Pattern` shows through between cards.
 */
const gridStyle = {
  "position": "relative",
  "zIndex": "10",
  "gap": "var(--spacing-md)",
  "rowGap": "var(--spacing-2xl)",
  "padding": "var(--spacing-md)",
  "@media (min-width: 1024px)": {
    "paddingLeft": "var(--spacing-xl)",
    "paddingRight": "var(--spacing-xl)",
    "paddingTop": "var(--spacing-2xl)",
    "paddingBottom": "var(--spacing-2xl)"
  }
};
const cellStyle = {
  "gridColumn": "1 / -1",
  "@media (min-width: 1024px)": {
    "gridColumn": "span 4 / span 4"
  }
};
const fetcher: Fetcher<CaseStudiesPage, string> = async url => {
  const response = await fetch(url);
  const body = (await response.json()) as CaseStudiesPage;
  if (!response.ok || body.success !== true) {
    throw new Error("Could not load case studies. Please try again.");
  }
  return body;
};

/** Infinite Case Studies grid with the established cards → map → cards layout. */
export function CaseStudiesGrid({
  limit,
  initialCaseStudies,
  total,
  activeIndustry,
  middleBlocks,
  onClearFilters
}: Props) {
  const themeStyle = getThemeStyle({
    mode: "light"
  });
  const hasActiveFilters = Boolean(activeIndustry);
  const {
    data,
    error,
    isLoading,
    mutate,
    size,
    setSize
  } = useSWRInfinite(pageIndex => buildCaseStudiesUrl(pageIndex * limit, limit, activeIndustry), fetcher, {
    revalidateFirstPage: hasActiveFilters,
    revalidateOnMount: hasActiveFilters,
    fallbackData: hasActiveFilters ? undefined : [{
      success: true,
      data: initialCaseStudies,
      total
    }],
    parallel: true
  });
  const pages = React.useMemo(() => data ?? [], [data]);
  const caseStudies = React.useMemo(() => pages.flatMap(page => page.data ?? []), [pages]);
  const isLoadingMore = isLoading || size > 0 && pages[size - 1] === undefined;
  const lastPage = pages[pages.length - 1];
  const isLastPageNotFull = (lastPage?.data?.length ?? 0) < limit;
  const activeTotal = pages[0]?.total ?? total;
  const hasMore = caseStudies.length < activeTotal && !isLastPageNotFull;
  const loadMoreRef = useInfiniteScroll({
    hasMore: hasMore && !error,
    isLoading: isLoadingMore,
    onLoadMore: () => setSize(current => current + 1)
  });
  const isInitialLoading = !hasItems(caseStudies) && isLoadingMore && !error;
  const hasNoResults = !hasItems(caseStudies) && !isLoadingMore && !error;
  const statusMessage = error ? "Case studies could not be loaded." : isLoadingMore ? "Loading case studies." : hasNoResults ? "No case studies found." : `${activeTotal} ${activeTotal === 1 ? "case study" : "case studies"} found. ${caseStudies.length} shown.`;
  const firstBatch = caseStudies.slice(0, 3);
  const secondBatch = caseStudies.slice(3);
  return (
    // Unpainted so the route's dotted Pattern reads through the listing; the
    // white page background comes from `useBodyBackground` on the overview view.
    <div role="region" aria-label="Case studies" aria-busy={isLoadingMore}>
      <_StyledP role="status" aria-live="polite" aria-atomic="true">
        {statusMessage}
      </_StyledP>

      {isInitialLoading ? <_StyledDiv>Loading case studies…</_StyledDiv> : hasNoResults ? <_StyledDiv2 $_css={[themeStyle.background, themeStyle.border]}>
          <Wrap mode="light">
            <_StyledDiv3>
              <NoResults onClear={onClearFilters} />
            </_StyledDiv3>
          </Wrap>
        </_StyledDiv2> : <_StyledWrap mode="light">
          <div>
            {hasItems(firstBatch) && <_StyledGrid $_css2={[gridStyle]}>
                {firstBatch.map((caseStudy, index) => <_StyledDiv4 key={caseStudy._id ?? index} $_css3={[cellStyle]}>
                    <CaseStudyCard caseStudy={caseStudy} />
                  </_StyledDiv4>)}
              </_StyledGrid>}
            <CaseStudiesMap caseStudies={caseStudies} />
            {middleBlocks}
            {hasItems(secondBatch) && <_StyledGrid2 $_css4={[gridStyle]}>
                {secondBatch.map((caseStudy, index) => <_StyledDiv5 key={caseStudy._id ?? index} $_css5={[cellStyle]}>
                    <CaseStudyCard caseStudy={caseStudy} />
                  </_StyledDiv5>)}
              </_StyledGrid2>}
          </div>
        </_StyledWrap>}

      {error && <_StyledDiv6 role="alert">
          <p>{error.message}</p>
          <_StyledButton onClick={() => mutate()} type="button">
            Try again
          </_StyledButton>
        </_StyledDiv6>}

      {hasMore && !error && <div ref={loadMoreRef}>
          <LoadMore isLoading={isLoadingMore} onClick={() => setSize(current => current + 1)} />
        </div>}
    </div>
  );
}
var _StyledP = _styled("p").withConfig({
  componentId: "sc-sf-14c3vst-0"
})({
  "position": "absolute",
  "width": "1px",
  "height": "1px",
  "padding": "0",
  "margin": "-1px",
  "overflow": "hidden",
  "clip": "rect(0, 0, 0, 0)",
  "whiteSpace": "nowrap",
  "borderWidth": "0"
});
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-14c3vst-1"
})({
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))",
  "padding": "2.4rem",
  "textAlign": "center"
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-14c3vst-2"
})`${p => p.$_css}`;
var _StyledDiv3 = _styled4("div").withConfig({
  componentId: "sc-sf-14c3vst-3"
})({
  "padding": "2.4rem",
  "@media (min-width: 1024px)": {
    "padding": "8rem"
  }
});
var _StyledWrap = _styled5(Wrap).withConfig({
  componentId: "sc-sf-14c3vst-4"
})({
  "borderWidth": "0px"
});
var _StyledGrid = _styled6(Grid).withConfig({
  componentId: "sc-sf-14c3vst-5"
})`${p => p.$_css2}`;
var _StyledDiv4 = _styled7("div").withConfig({
  componentId: "sc-sf-14c3vst-6"
})`${p => p.$_css3}`;
var _StyledGrid2 = _styled8(Grid).withConfig({
  componentId: "sc-sf-14c3vst-7"
})`${p => p.$_css4}`;
var _StyledDiv5 = _styled9("div").withConfig({
  componentId: "sc-sf-14c3vst-8"
})`${p => p.$_css5}`;
var _StyledDiv6 = _styled10("div").withConfig({
  componentId: "sc-sf-14c3vst-9"
})({
  "borderTopWidth": "1px",
  "borderColor": "var(--stroke-primary)",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))",
  "padding": "2.4rem",
  "textAlign": "center"
});
var _StyledButton = /*#__PURE__*/_styled11("button").withConfig({
  displayName: "case-studies-grid___StyledButton",
  componentId: "sc-1bgzdl2-0"
})({
  "marginTop": "1.2rem",
  "textDecorationLine": "underline"
});